import React from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import {StyledEngineProvider, ThemeProvider as MuiThemeProvider} from '@mui/material/styles'
import {ThemeProvider} from 'styled-components'
import {buildMuiTheme} from './mui-theme'
import {ThemeModeProvider, useThemeMode} from './ThemeMode.provider'
import {theme} from '~/src/design-system/theme'

// Design-system tokens — single source of truth for CSS custom properties.
// Replaces the legacy GlobalCSSVariables createGlobalStyle block (Wave 1).
import '~/src/design-system/colors_and_type.css'

// Hoist this. Doesn't need to be re-created on each render.
const inputCssBaseline = <CssBaseline />

/**
 * Everything below ThemeModeProvider, so it can read the resolved theme.
 *
 * NOTE on the styled-components theme (`~/src/design-system/theme`): it is a
 * flat light-mode literal palette and is NOT theme-aware. That is deliberate
 * and currently harmless — `theme.colors.*` is referenced only by
 * design-system/PatternComponents.tsx (reference/prototype components). All
 * production components read CSS variables, which do switch. If the JS theme
 * ever gains production callers it needs a dark twin.
 */
function UiThemeInner({children}: {children: React.ReactNode}) {
  const {resolvedTheme} = useThemeMode()

  // 'light' returns the same object every time, so light-mode consumers never
  // see a theme identity change and MUI never regenerates its styles.
  const activeMuiTheme = React.useMemo(() => buildMuiTheme(resolvedTheme), [resolvedTheme])

  return (
    <MuiThemeProvider theme={activeMuiTheme}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          {inputCssBaseline}
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </MuiThemeProvider>
  )
}

export function UiThemeProvider({children}: {children: React.ReactNode}) {
  return (
    <ThemeModeProvider>
      <UiThemeInner>{children}</UiThemeInner>
    </ThemeModeProvider>
  )
}
